import { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBrainStore } from '@/state/brainStore';
import { useSessionStore } from '@/state/sessionStore';
import QueueItem from '@/components/QueueItem';
import DashboardKPITile from '@/components/DashboardKPITile';
import ActivityFeedItem from '@/components/ActivityFeedItem';
import { aud, customerName, simTimeFull } from '@/utils/format';
import { AWAITING_CLIENT } from '@/domain/selfBookFlow';
import {
  ListTodo,
  Calendar,
  Users,
  Wallet,
  PhoneMissed,
  Hash,
  Footprints,
} from 'lucide-react';
import clsx from 'clsx';
import WalkInIntake from '@/components/WalkInIntake';

type Tab = 'queue' | 'calendar' | 'artists' | 'deposits' | 'missed' | 'comms' | 'front-desk';

const TABS: Array<{ id: Tab; label: string; Icon: typeof Calendar }> = [
  { id: 'queue', label: 'Queue', Icon: ListTodo },
  { id: 'calendar', label: 'Calendar', Icon: Calendar },
  { id: 'artists', label: 'Artists', Icon: Users },
  { id: 'deposits', label: 'Deposits', Icon: Wallet },
  { id: 'missed', label: 'Missed calls', Icon: PhoneMissed },
  { id: 'comms', label: 'Internal comms', Icon: Hash },
  { id: 'front-desk', label: 'Front desk', Icon: Footprints },
];

export default function StudioView() {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();
  const studios = useBrainStore((s) => s.studios);
  const customers = useBrainStore((s) => s.customers);
  const artists = useBrainStore((s) => s.artists);
  const deposits = useBrainStore((s) => s.deposits);
  const internalComms = useBrainStore((s) => s.internalComms);
  const activity = useBrainStore((s) => s.activityFeed);
  const setActiveStudio = useSessionStore((s) => s.setActiveStudio);

  const studio = studioId ? studios[studioId] : undefined;
  const [tab, setTab] = useState<Tab>('queue');

  const studioCustomers = useMemo(
    () => Object.values(customers).filter((c) => c.primaryStudioId === studioId),
    [customers, studioId],
  );

  const studioArtists = useMemo(
    () => Object.values(artists).filter((a) => a.studioId === studioId),
    [artists, studioId],
  );

  const studioDeposits = useMemo(
    () => Object.values(deposits).filter((d) => d.studioId === studioId),
    [deposits, studioId],
  );

  if (!studio) {
    return (
      <section className="px-10 py-12">
        <p className="font-serif text-cream-soft">Unknown studio {studioId}.</p>
      </section>
    );
  }

  return (
    <section className="px-10 py-8" data-testid={`studio-view-${studio.id}`}>
      <header className="max-w-6xl flex items-end justify-between">
        <div>
          <p className="label-small-caps text-gold-bright">{studio.brand}</p>
          <h1 className="heading-display text-3xl text-cream mt-2">{studio.name}</h1>
          <p className="font-inter text-xs text-cream-soft mt-2 tracking-widest uppercase">
            {studio.state} · {studio.brandedNumber} · {studio.artistCount} artists · {studio.address}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => {
              setActiveStudio(studio.id);
              navigate(`/calendar/${studio.id}`);
            }}
            className="btn-ghost text-xs"
          >
            Open calendar
          </button>
        </div>
      </header>

      <nav className="mt-6 flex gap-2 flex-wrap border-b border-gold-dim/20 pb-2 max-w-6xl">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`studio-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={clsx(
              'px-4 py-1.5 text-xs uppercase tracking-widest rounded-full border transition',
              tab === t.id
                ? 'border-gold-bright text-gold-bright bg-gold/10'
                : 'border-transparent text-cream-soft hover:text-cream hover:border-gold-dim/40',
            )}
          >
            <span className="inline-flex items-center gap-2">
              <t.Icon size={12} /> {t.label}
            </span>
          </button>
        ))}
      </nav>

      <div className="mt-6 max-w-6xl">
        {tab === 'queue' ? (
          <div className="grid grid-cols-3 gap-3 mb-6">
            <DashboardKPITile label="Today's inquiries" value={studioCustomers.length.toString()} />
            <DashboardKPITile label="Deposit-ready" value={studioCustomers.filter((c) => c.leadStage === 'deposit-pending').length.toString()} accent />
            <DashboardKPITile label="Confirmations tomorrow" value="23" />
          </div>
        ) : null}

        {tab === 'queue' ? (
          <ol className="space-y-3">
            {studioCustomers.map((c) => {
              const last = c.conversation[c.conversation.length - 1];
              if (!last) return null;
              return (
                <li key={c.id}>
                  <QueueItem
                    customer={c}
                    channel={last.channel}
                    intent={
                      c.designIntent
                        ? `${c.designIntent.type}, ${c.designIntent.placement}, ${c.designIntent.size}`
                        : last.body.slice(0, 100)
                    }
                    recommendedAction={c.leadStage === 'deposit-pending' ? 'Callback now' : 'Continue chat'}
                    lastEventIso={last.timestamp}
                    onClick={() => navigate(`/customer/${c.id}`)}
                  />
                </li>
              );
            })}
          </ol>
        ) : null}

        {tab === 'calendar' ? (
          <div className="panel p-8 text-center">
            <Calendar size={32} className="text-gold-bright mx-auto" />
            <p className="font-inter text-cream mt-3">Open the dedicated Studio Calendar.</p>
            <button
              className="btn-primary mt-4 text-xs"
              onClick={() => navigate(`/calendar/${studio.id}`)}
            >
              Open {studio.name} calendar
            </button>
          </div>
        ) : null}

        {tab === 'artists' ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {studioArtists.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => navigate(`/artist/${a.id}`)}
                className="panel p-4 text-left hover:border-gold-bright/40 transition"
              >
                <p className="font-inter text-cream">{a.displayName}</p>
                <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                  {a.instagramHandle}
                </p>
                <p className="font-inter text-xs text-cream-soft mt-3">{a.bio}</p>
                <div className="flex flex-wrap gap-1 mt-3">
                  {a.styleSpeciality.map((s) => (
                    <span key={s} className="pill-cream text-[10px]">
                      {s}
                    </span>
                  ))}
                </div>
                {a.isApprentice ? <p className="mt-3 pill-gold text-[10px]">Apprentice</p> : null}
              </button>
            ))}
          </div>
        ) : null}

        {tab === 'deposits' ? (
          <ol className="space-y-2">
            {studioDeposits.length === 0 ? (
              <li className="font-serif text-cream-soft">
                No deposits at {studio.name} yet today. Scenario 1 lands the first one.
              </li>
            ) : null}
            {studioDeposits.map((d) => {
              const cust = customers[d.customerId];
              return (
                <li key={d.id} className="panel p-3 flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm text-cream">
                      {cust
                        ? customerName(cust)
                        : d.customerId === AWAITING_CLIENT
                          ? 'Awaiting client'
                          : d.customerId}
                    </p>
                    <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                      {d.stripePaymentLinkId} · {simTimeFull(d.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-gold-bright">{aud(d.amountAud)}</span>
                    <span className="pill-cream text-[10px]">{d.status}</span>
                  </div>
                </li>
              );
            })}
          </ol>
        ) : null}

        {tab === 'missed' ? (
          <ol className="space-y-2">
            {studioCustomers
              .filter((c) => c.conversation.some((m) => m.channel === 'voice' && m.direction === 'in'))
              .map((c) => (
                <li key={c.id} className="panel p-3 flex items-center justify-between">
                  <div>
                    <p className="font-inter text-sm text-cream">{customerName(c)}</p>
                    <p className="font-inter text-[10px] text-cream-soft uppercase tracking-widest mt-1">
                      {c.phone} · voicemail captured
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate(`/customer/${c.id}`)}
                    className="btn-ghost text-xs"
                  >
                    Open record
                  </button>
                </li>
              ))}
          </ol>
        ) : null}

        {tab === 'comms' ? (
          <div className="space-y-3">
            {Object.values(internalComms)
              .filter(
                (ch) =>
                  ch.scope === 'studio-ops' || ch.scope === 'studio-artists' || ch.scope === 'studio-apprentices'
                    ? ch.studioId === studio.id
                    : true,
              )
              .map((channel) => (
                <div key={channel.id} className="panel p-4">
                  <p className="font-inter text-sm text-gold-bright">{channel.name}</p>
                  {channel.messages.length === 0 ? (
                    <p className="font-serif text-cream-soft text-xs mt-2">Quiet.</p>
                  ) : (
                    channel.messages.map((m) => (
                      <p key={m.id} className="text-xs text-cream-soft mt-2 leading-relaxed">
                        <span className="font-inter text-gold uppercase tracking-widest text-[10px] mr-2">
                          {m.authorId}
                        </span>
                        {m.body}
                      </p>
                    ))
                  )}
                </div>
              ))}
          </div>
        ) : null}

        {tab === 'front-desk' ? <WalkInIntake studio={studio} /> : null}
      </div>

      <section className="mt-10 max-w-6xl">
        <h2 className="heading-serif text-lg text-cream mb-3">Studio activity</h2>
        <div className="space-y-2">
          {activity
            .filter((a) => !a.studioId || a.studioId === studio.id)
            .slice(0, 6)
            .map((entry) => (
              <ActivityFeedItem key={entry.id} entry={entry} />
            ))}
        </div>
      </section>
    </section>
  );
}
